import "./code.css"
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { htmlSafe } from '@ember/template';
import { link } from 'reactiveweb/link';
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { waitFor, waitForPromise } from '@ember/test-waiters';
import { modifier } from 'ember-modifier';
import { connectToChild } from 'penpal';
import { g, i, n } from 'decorator-transforms/runtime-esm';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

;

/**
 * We can't post right away, because we might do so before the iframe is ready.
 * We need to wait until the frame initiates contact.
 */
function PostMessage(handleUpdate) {
  return modifier((element, [data]) => {
    // In SSR (happy-dom), contentWindow exists but can't do real messaging.
    // Skip to avoid triggering @waitFor-decorated queuePayload, whose
    // connection.promise never resolves and blocks settled() indefinitely.
    if (!element.contentWindow || import.meta.env?.SSR) return;
    handleUpdate(data);
  });
}
function HandleMessage(createConnection) {
  return modifier(element => {
    if (import.meta.env?.SSR) return;
    return createConnection(element);
  });
}
class HostMessaging {
  static {
    g(this.prototype, "frameStatus", [tracked]);
  }
  #frameStatus = (i(this, "frameStatus"), void 0);
  /**
   * We can't post right away, because we might do so before the iframe is ready.
   * We need to wait until the frame initiates contact.
   */
  postMessage = PostMessage(data => void this.queuePayload(data));
  onMessage = HandleMessage(element => {
    this.connection = connectToChild({
      iframe: element,
      methods: {}
    });
    waitForPromise(this.connection.promise).catch(console.error);
    return () => void this.connection?.destroy();
  });
  async queuePayload(data) {
    await Promise.resolve();
    if (isDestroyed(this) || isDestroying(this)) return;
    if (!this.connection) return;
    const child = await this.connection.promise;
    if (isDestroyed(this) || isDestroying(this)) return;
    await child.update(data);
  }
  static {
    n(this.prototype, "queuePayload", [waitFor]);
  }
}

const DEFAULT_DEMO = `
// Welcome
//
// Whatever is export-defaulted is what is rendered
<template>
  hello world!
</template>
`;
const DEFAULT_NUMBER_OF_LINES = 7;
const HOST = 'https://limber.glimdown.com/edit';
/**
 * NOTE: updates the the `src` URL do not update the iframe
 */
const INITIAL_URL = options => {
  const {
    code,
    editor,
    shadowdom,
    nohighlight,
    format,
    editorLoad
  } = options;
  const initialQPs = new URLSearchParams(window.location.search);
  const local = initialQPs.get('local');
  const host = initialQPs.has('local') ? `https://localhost:${local || '4201'}/edit` : HOST;
  return `${host}?` + (editorLoad ? `&editorLoad=${editorLoad}` : '') + (editor ? `&editor=${editor}` : '') + (shadowdom ? `&shadowdom=${shadowdom}` : '') + (format ? `&format=${format}` : '&format=BUG:MISSING') + (nohighlight ? `&nohighlight=${nohighlight}` : '') + (local ? `&local` : '') + (code ? `&t=${encodeURIComponent(code)}` : DEFAULT_DEMO);
};
function defaultStyle(lines) {
  return `height: calc(1.5rem * ${lines});`;
}
class Code extends Component {
  static {
    g(this.prototype, "messaging", [link(HostMessaging)]);
  }
  #messaging = (i(this, "messaging"), void 0);
  static {
    g(this.prototype, "manualLoad", [tracked], function () {
      return false;
    });
  }
  #manualLoad = (i(this, "manualLoad"), void 0);
  initialData;
  constructor(owner, args) {
    super(owner, args);
    this.initialData = {
      editorLoad: this.args.editorLoad,
      editor: this.args.editor,
      nohighlight: this.args.nohighlight,
      shadowdom: this.args.shadowdom,
      code: this.code,
      format: this.format
    };
  }
  get load() {
    if (this.args.clickToLoad) {
      return this.manualLoad;
    }
    return true;
  }
  get code() {
    if ('code' in this.args) {
      return this.args.code;
    }
    return '';
  }
  get format() {
    if ('format' in this.args) {
      return this.args.format ?? 'gjs';
    }
    return 'gjs';
  }
  get lines() {
    return this.args.lines ?? this.code?.split('\n')?.length ?? DEFAULT_NUMBER_OF_LINES;
  }
  get title() {
    return this.args.title ?? guidFor(this.code);
  }
  get liveData() {
    return {
      code: this.code,
      format: this.format,
      shadowdom: this.args.shadowdom
    };
  }
  triggerLoad = () => {
    this.manualLoad = true;
  };
  /**
  * This uses iframe postMessage to efficiently update state within the
  * iframe's app so that we don't have to reload the whole app if we want to change the
  * URL / code.
  */
  static {
    setComponentTemplate(precompileTemplate("{{#if this.load}}\n  <iframe loading=\"lazy\" data-lines=\"{{this.lines}}\" {{this.messaging.postMessage this.liveData}} {{this.messaging.onMessage}} title={{this.title}} src={{htmlSafe (INITIAL_URL this.initialData)}} style={{htmlSafe (defaultStyle this.lines)}} ...attributes>\n  </iframe>\n{{else}}\n  <div class=\"limber__code__click-to-load\">\n    <button class=\"limber__code__click-to-load__button\" type=\"button\" {{on \"click\" this.triggerLoad}}>\n      Click to Load REPL\n    </button>\n  </div>\n{{/if}}", {
      strictMode: true,
      scope: () => ({
        htmlSafe,
        INITIAL_URL,
        defaultStyle,
        on
      })
    }), this);
  }
}

export { Code as REPL };
//# sourceMappingURL=index.js.map
